import { ContactLocation } from "domain/ContactLocation";
import { HTTPClientService } from "services/HTTPClient";
import { getAPIBase } from "../util/MagnoliaAppHelpers";

const basePath = getAPIBase();
const restBaseLocations = basePath + process.env.REACT_APP_MGNL_API_LOCATIONS;

interface LocationsApiResponse {
  total?: number;
  results?: Array<Record<string, unknown>>;
}

export default class LocationService {
  readonly httpClientService: HTTPClientService;

  constructor(httpClientService: HTTPClientService) {
    this.httpClientService = httpClientService;
  }

  loadLocations = async (
    ancestor = "/",
    locale?: string
  ): Promise<ContactLocation[]> => {
    try {
      const httpClient = this.httpClientService.getAlbisHttpClient();

      const pageSize = 100;
      let offset = 0;
      let total = Infinity;
      const results: Array<Record<string, unknown>> = [];

      // The v2 delivery endpoint clamps each response to its configured maxLimit
      // (100, regardless of the requested `limit`), so page through with `offset`
      // until we've collected every result.
      while (offset < total) {
        const params = new URLSearchParams({
          limit: String(pageSize),
          offset: String(offset),
          "@ancestor": ancestor,
        });
        // Magnolia i18n on delivery endpoints is driven by the `lang` query
        // param (DefaultI18nContentSupport), which returns the localized value
        // in the plain property (e.g. `title`). The endpoint does NOT expose
        // suffixed keys like `title-en`, so the mapping below reads plain keys.
        if (locale) params.set("lang", locale);

        const { data } = await httpClient.get<LocationsApiResponse>(
          `${restBaseLocations}?${params.toString()}`
        );

        const pageResults = data?.results ?? [];
        results.push(...pageResults);
        total = data?.total ?? results.length;

        // Advance by what the server actually returned, not what we asked for —
        // the endpoint may clamp the page to a smaller configured limit.
        if (pageResults.length === 0) break;
        offset += pageResults.length;
      }

      return results.map((node) => this.mapNodeToContactLocation(node));
    } catch {
      console.log("[LocationService] API not reachable");
      return [] as ContactLocation[];
    }
  };

  private mapNodeToContactLocation(
    node: Record<string, unknown>
  ): ContactLocation {
    // Values are already localized by the endpoint via the `lang` param, so we
    // read the plain properties directly.
    const str = (key: string): string | undefined =>
      (node[key] as string) ?? undefined;
    return {
      "@id": str("@id") ?? "",
      title: str("title") ?? "",
      subtitle: str("subtitle"),
      name: str("companyName") ?? str("name"),
      address: str("address"),
      city: str("city"),
      zipCode: str("zipCode"),
      country: str("country"),
      phoneNumber: str("phoneNumber"),
      email: str("email"),
      fax: str("fax"),
    };
  }
}
